import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as Yup from 'yup'
import { useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import clsx from 'clsx'
import OtpInput from 'react-otp-input'

import { Alert, PasswordMaskField } from 'components/common'
import { SocialLoginButtons } from 'containers/common'
import { useAuth } from 'hooks/useAuth'
import { objectToSearchString } from 'serialize-query-params'
import { AuthResponse } from 'models/response'
import { isPhoneNumber } from 'utils/common'
import PasskeyAuth from 'containers/passkey-auth'
import { Button } from 'components/atoms/button'
import { Turnstile } from 'components/atoms/turnstile'
import { useTurnstile } from 'hooks/useTurnstile'

const usernameStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your email, username, or phone number'),
})

const passwordStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your email, username, or phone number'),
	password: Yup.string().required('Please enter your password'),
})

const otpStepSchema = Yup.object().shape({
	userName: Yup.string().required('Please enter your phone number'),
	otpCode: Yup.string()
		.required('Please enter the verification code')
		.length(6, 'Verification code must be 6 digits'),
})

interface LoginFormProps {
	initialEmail?: string
	onSuccess?: (rs: AuthResponse) => void
	isModal?: boolean
	onSwitchToSignUp?: () => void
	inviteId?: string
	fromForgotPassword?: boolean
	redirectTo?: string | null
}

interface LoginFormData {
	userName: string
	password?: string
	otpCode?: string
}

export const LoginForm = ({
	initialEmail,
	onSuccess,
	isModal = false,
	onSwitchToSignUp,
	inviteId,
	fromForgotPassword = false,
	redirectTo = null,
}: LoginFormProps) => {
	const searchParamsString = objectToSearchString({ to: redirectTo })
	const [message, setMessage] = useState<any>(null)
	const [submitting, setSubmitting] = useState(false)
	const [currentStep, setCurrentStep] = useState<'username' | 'credentials'>('username')
	const [inputType, setInputType] = useState<'phone' | 'email_username'>('email_username')
	const [otpSent, setOtpSent] = useState(false)
	const [nextResendTime, setNextResendTime] = useState<Date | null>(null)
	const [phoneCodeExpiredDate, setPhoneCodeExpiredDate] = useState<Date | null>(() => {
		try {
			const stored = localStorage.getItem('phoneCodeExpiredDate')
			if (stored) {
				const parsed = JSON.parse(stored)
				return parsed ? new Date(parsed) : null
			}
		} catch (error) {
			console.warn('Failed to parse phoneCodeExpiredDate from localStorage:', error)
		}
		return null
	})
	const [countdown, setCountdown] = useState<number>(0)
	const [resendCountdown, setResendCountdown] = useState<number>(0)
	const [failedAttempts, setFailedAttempts] = useState<number>(0)
	const [blockedUntil, setBlockedUntil] = useState<Date | null>(null)
	const [blockCountdown, setBlockCountdown] = useState<number>(0)
	const { login, phoneLogin, phoneVerifyOtp, isPasskeySupported } = useAuth()
	const turnstile = useTurnstile()

	const safeSetPhoneCodeExpiredDate = (date: Date | null) => {
		setPhoneCodeExpiredDate(date)
		try {
			if (date) {
				localStorage.setItem('phoneCodeExpiredDate', JSON.stringify(date.toISOString()))
			} else {
				localStorage.removeItem('phoneCodeExpiredDate')
			}
		} catch (error) {
			console.warn('Failed to save phoneCodeExpiredDate to localStorage:', error)
		}
	}

	const getCurrentValidationSchema = () => {
		if (currentStep === 'username') {
			return usernameStepSchema
		}
		return inputType === 'phone' ? otpStepSchema : passwordStepSchema
	}

	const {
		control,
		register,
		handleSubmit,
		watch,
		reset,
		formState: { errors, isValid, isDirty },
	} = useForm<LoginFormData>({
		resolver: yupResolver(getCurrentValidationSchema()),
		mode: 'onChange',
		defaultValues: {
			userName: initialEmail ?? '',
			password: '',
			otpCode: '',
		},
	})

	const watchedUserName = watch('userName')

	const isContinueButtonEnabled = () => {
		if (submitting) return false

		if (blockedUntil && blockCountdown > 0) return false

		if (currentStep === 'username') {
			return isValid && watchedUserName && watchedUserName.trim().length > 0
		} else {
			return isValid && isDirty
		}
	}

	useEffect(() => {
		if (fromForgotPassword) {
			setMessage({
				description:
					'If an account with the email you entered exists, you will receive instructions to reset your password shortly.',
				type: 'success',
			})
		}
	}, [fromForgotPassword])

	useEffect(() => {
		const expiredDate = phoneCodeExpiredDate
		if (!expiredDate) return

		const updateCountdown = () => {
			const now = new Date()
			const timeLeft = Math.max(0, Math.floor((expiredDate.getTime() - now.getTime()) / 1000))
			setCountdown(timeLeft)

			if (timeLeft <= 0) {
				safeSetPhoneCodeExpiredDate(null)
			}
		}

		updateCountdown()
		const interval = setInterval(updateCountdown, 1000)

		return () => clearInterval(interval)
	}, [phoneCodeExpiredDate])

	// Next resend countdown timer effect
	useEffect(() => {
		if (!nextResendTime) return

		const updateNextResendCountdown = () => {
			const now = new Date()
			const timeLeft = Math.max(0, Math.floor((nextResendTime.getTime() - now.getTime()) / 1000))
			setResendCountdown(timeLeft)

			if (timeLeft <= 0) {
				setNextResendTime(null)
			}
		}

		updateNextResendCountdown()
		const interval = setInterval(updateNextResendCountdown, 1000)

		return () => clearInterval(interval)
	}, [nextResendTime])

	// Block countdown timer effect
	useEffect(() => {
		if (!blockedUntil) return

		const updateBlockCountdown = () => {
			const now = new Date()
			const timeLeft = Math.max(0, Math.floor((blockedUntil.getTime() - now.getTime()) / 1000))
			setBlockCountdown(timeLeft)

			if (timeLeft <= 0) {
				setBlockedUntil(null)
				setFailedAttempts(0)
				setMessage({
					description: 'You can now try again.',
					type: 'info',
				})
			}
		}

		updateBlockCountdown()
		const interval = setInterval(updateBlockCountdown, 1000)

		return () => clearInterval(interval)
	}, [blockedUntil])

	const formatCountdown = (seconds: number): string => {
		const minutes = Math.floor(seconds / 60)
		const remainingSeconds = seconds % 60
		return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
	}

	const formatBlockCountdown = (seconds: number): string => {
		const hours = Math.floor(seconds / 3600)
		const minutes = Math.floor((seconds % 3600) / 60)
		const remainingSeconds = seconds % 60

		if (hours > 0) {
			return `${hours}h ${minutes}m ${remainingSeconds}s`
		}
		return `${minutes}m ${remainingSeconds}s`
	}

	const handleUsernameStep = (values: LoginFormData) => {
		setMessage(null)
		const { userName } = values

		if (isPhoneNumber(userName)) {
			setInputType('phone')
			setSubmitting(true)

			setFailedAttempts(0)
			setBlockedUntil(null)
			setBlockCountdown(0)

			phoneLogin(
				{ phoneNumber: userName }, // Phone login doesn't need password
				(rs: AuthResponse) => {
					setSubmitting(false)
					setCurrentStep('credentials')
					setOtpSent(true)
					reset({ userName, otpCode: '' })

					if (rs.nextResend) {
						const nextResendAt = new Date(rs.nextResend)
						setNextResendTime(nextResendAt)
					}

					if (rs.expiredDate) {
						const expiredDateAt = new Date(rs.expiredDate)
						safeSetPhoneCodeExpiredDate(expiredDateAt)
					}

					if (rs.blockedUntil) {
						const blockedUntilDate = new Date(rs.blockedUntil)
						setBlockedUntil(blockedUntilDate)
					}
				},
				(error: string) => {
					setSubmitting(false)
					if (error.toLowerCase().includes('otp') || error.toLowerCase().includes('verification')) {
						setCurrentStep('credentials')
						setOtpSent(true)
						reset({ userName, otpCode: '' })
						setMessage({
							description: 'Verification code sent to your phone',
							type: 'success',
						})
					} else {
						setMessage({
							description: error,
							type: 'danger',
						})
					}
				},
				isModal
			)
		} else {
			setInputType('email_username')
			setCurrentStep('credentials')
			reset({ userName, password: '' })
		}
	}

	const handleCredentialsStep = (values: LoginFormData) => {
		setSubmitting(true)
		setMessage(null)

		if (inputType === 'phone') {
			if (!values.otpCode) {
				setSubmitting(false)
				setMessage({
					description: 'Please enter the verification code',
					type: 'danger',
				})
				return
			}

			phoneVerifyOtp(
				{ phoneNumber: values.userName, code: values.otpCode },
				(rs: AuthResponse) => {
					setSubmitting(false)
					setFailedAttempts(0)
					setBlockedUntil(null)
					setBlockCountdown(0)
					if (onSuccess) {
						onSuccess(rs)
					}
				},
				(_error: string) => {
					setSubmitting(false)
					const newFailedAttempts = failedAttempts + 1
					setFailedAttempts(newFailedAttempts)

					if (newFailedAttempts >= 3) {
						const blockUntil = new Date(Date.now() + 30 * 60 * 1000)
						setBlockedUntil(blockUntil)
						setMessage({
							description: `Too many failed attempts. You are blocked for 30 minutes.`,
							type: 'danger',
						})
					} else {
						const remainingAttempts = 3 - newFailedAttempts
						setMessage({
							description: `Invalid verification code. ${remainingAttempts} attempt${
								remainingAttempts !== 1 ? 's' : ''
							} remaining.`,
							type: 'danger',
						})
					}
				}
			)
		} else {
			if (!values.password) {
				setSubmitting(false)
				setMessage({
					description: 'Please enter your password',
					type: 'danger',
				})
				return
			}

			login(
				{ userName: values.userName, password: values.password },
				turnstile.token,
				(rs: AuthResponse) => {
					setSubmitting(false)
					turnstile.reset()
					if (onSuccess) {
						onSuccess(rs)
					}
				},
				(error: string) => {
					setSubmitting(false)
					turnstile.reset()
					setMessage({
						description: error,
						type: 'danger',
					})
				},
				isModal
			)
		}
	}

	const onSubmit = (values: LoginFormData) => {
		if (currentStep === 'username') {
			handleUsernameStep(values)
		} else {
			handleCredentialsStep(values)
		}
	}

	const handleResendOtp = () => {
		setSubmitting(true)
		phoneLogin(
			{ phoneNumber: watchedUserName },
			(rs: AuthResponse) => {
				setSubmitting(false)
				setMessage({
					description: 'Verification code has been resent',
					type: 'success',
				})

				if (rs.nextResend) {
					const nextResendAt = new Date(rs.nextResend)
					setNextResendTime(nextResendAt)
				}

				if (rs.expiredDate) {
					const expiredDateAt = new Date(rs.expiredDate)
					safeSetPhoneCodeExpiredDate(expiredDateAt)
				}

				if (rs.blockedUntil) {
					const blockedUntilDate = new Date(rs.blockedUntil)
					setBlockedUntil(blockedUntilDate)
				}
			},
			(error: string) => {
				setSubmitting(false)
				setMessage({
					description: error,
					type: 'danger',
				})
			}
		)
	}

	const getSignUpUrl = () => {
		return `/auth/sign-up${searchParamsString ? `?${searchParamsString}` : ''}`
	}

	return (
		<div className={clsx(isModal ? '' : 'w-full')}>
			<div className={clsx(isModal ? '' : 'w-full')}>
				{!isModal && (
					<h1 className="text-3xl font-bold text-center text-white mb-4">
						{currentStep === 'username'
							? 'Login'
							: inputType === 'phone'
								? 'Enter Verification Code'
								: 'Enter Password'}
					</h1>
				)}
				<Alert message={message} />
				<div className="mb-4">
					<div className="text-center text-white">
						Don't have an account?{' '}
						{isModal && onSwitchToSignUp ? (
							<Button variant="link" className="inline-flex" onClick={onSwitchToSignUp}>
								Sign up
							</Button>
						) : (
							<Link className="inline-flex" to={getSignUpUrl()} state={{ inviteId }}>
								<Button variant="link">Sign up</Button>
							</Link>
						)}
					</div>
				</div>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4 text-left">
						{/* Username field */}
						<div>
							{currentStep === 'username' ? (
								<div>
									<input
										id="username"
										type="text"
										className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
										placeholder="Email, Username, or Phone Number"
										autoComplete="username"
										{...register('userName')}
									/>
									{errors.userName && (
										<div className="text-secondary-red mt-2 text-sm">
											{errors.userName.message as string}
										</div>
									)}
								</div>
							) : (
								<div className="mb-2">
									<div className="flex items-center justify-between pb-3 px-4 border-b border-white-25">
										<div className="text-white text-lg font-medium">{watchedUserName}</div>
										<button
											type="button"
											onClick={() => {
												setCurrentStep('username')
												setInputType('email_username')
												setOtpSent(false)
												setNextResendTime(null)
												setCountdown(0)
												setResendCountdown(0)
												setFailedAttempts(0)
												setBlockedUntil(null)
												setBlockCountdown(0)
												setMessage(null)
												safeSetPhoneCodeExpiredDate(null)
												reset({ userName: watchedUserName, password: '', otpCode: '' })
											}}
											className="text-primary hover:text-primary-hover transition-colors"
											title="Change username"
										>
											<FontAwesomeIcon icon={['fas', 'edit']} size="lg" />
										</button>
									</div>
								</div>
							)}
						</div>

						{/* Credentials field - only shown in credentials step */}
						{currentStep === 'credentials' && (
							<div>
								{inputType === 'phone' ? (
									<div>
										<Controller
											control={control}
											name="otpCode"
											render={({ field }) => (
												<OtpInput
													value={field.value || ''}
													onChange={(otp: string) => field.onChange(otp)}
													numInputs={6}
													containerStyle="!grid grid-cols-6 gap-2"
													inputStyle={`px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block !w-12 h-12 text-center ${
														blockedUntil && blockCountdown > 0
															? 'opacity-50 cursor-not-allowed'
															: ''
													}`}
													renderInput={props => (
														<input
															{...props}
															type="tel"
															autoComplete="one-time-code"
															disabled={!!(blockedUntil && blockCountdown > 0)}
														/>
													)}
												/>
											)}
										/>
										<div
											className={clsx('text-white flex items-center justify-center mt-4', {
												'!text-secondary-red': countdown <= 0,
											})}
										>
											{formatCountdown(countdown)}
										</div>
										{otpSent && (
											<div className="mt-4 flex flex-col items-center justify-between gap-2">
												<p className="text-primary-text text-sm mb-2">
													Verification code sent to {watchedUserName}
												</p>
												{blockedUntil && blockCountdown > 0 ? (
													<div className="text-secondary-red text-sm">
														Account blocked. Try again in{' '}
														{formatBlockCountdown(blockCountdown)}
													</div>
												) : (
													<Button
														variant="link"
														onClick={handleResendOtp}
														disabled={submitting || resendCountdown > 0}
													>
														{submitting
															? 'Resending...'
															: resendCountdown > 0
																? `Resend in ${formatCountdown(resendCountdown)}`
																: 'Resend code'}
													</Button>
												)}
											</div>
										)}
									</div>
								) : (
									<div>
										<Controller
											control={control}
											name="password"
											render={({ field }) => (
												<PasswordMaskField
													id="password"
													className="px-4 py-3 border-2 border-translucent-plus-border bg-primary-surface/[0.4] backdrop-blur-lg rounded-lg text-primary-text placeholder-secondary-text placeholder:italic placeholder:font-light block w-full h-12"
													placeholder="Password"
													autoComplete="current-password"
													value={field.value ?? ''}
													onChange={field.onChange}
													onBlur={field.onBlur}
												/>
											)}
										/>
										{errors.password && (
											<div className="text-secondary-red mt-2 text-sm">
												{errors.password.message as string}
											</div>
										)}
									</div>
								)}
							</div>
						)}
					</div>
					{currentStep === 'credentials' && inputType === 'email_username' && (
						<div className="mt-4 flex justify-center">
							<Turnstile
								ref={turnstile.turnstileRef}
								onSuccess={turnstile.onSuccess}
								onError={turnstile.onError}
								onExpire={turnstile.onExpire}
								theme="dark"
							/>
						</div>
					)}
					<div className="mt-8">
						<Button
							variant="primary"
							fullWidth
							size="lg"
							type="submit"
							disabled={!isContinueButtonEnabled()}
						>
							{submitting && (
								<FontAwesomeIcon icon={['fas', 'spinner']} size="lg" spin className="mr-1" />
							)}
							{blockedUntil && blockCountdown > 0
								? `Blocked (${formatBlockCountdown(blockCountdown)})`
								: currentStep === 'username'
									? 'Continue'
									: inputType === 'phone'
										? 'Verify Code'
										: 'Sign In'}
						</Button>

						{/* Passkey Login Section - show in username step */}
						{currentStep === 'username' && isPasskeySupported() && (
							<>
								<div className="mt-6 flex items-center">
									<div className="flex-1 border-t border-white-25"></div>
									<span className="px-4 text-sm text-white/50">or</span>
									<div className="flex-1 border-t border-white-25"></div>
								</div>
								<div className="mt-6">
									<PasskeyAuth
										variant="login"
										onSuccess={(response: AuthResponse) => {
											if (onSuccess) {
												onSuccess(response)
											}
										}}
										onError={(error: string) => {
											setMessage({
												description: error,
												type: 'danger',
											})
										}}
										disabled={submitting}
									/>
								</div>
							</>
						)}
					</div>
				</form>
				{/* Forgot password link - only show for email/username credentials step */}

				<div className="mt-5 flex items-center justify-center">
					<Link to="/auth/forgot-password">
						<Button variant="link">Forgot your password?</Button>
					</Link>
				</div>

				{/* Social login - only show in username step */}
				{currentStep === 'username' && (
					<div className="mt-4">
						<h3 className="text-center text-white mb-6">Or Log in via</h3>
						<SocialLoginButtons isModal={isModal} />
					</div>
				)}
			</div>
		</div>
	)
}
